import { useState } from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ROSTER } from './tokens';

type Scale = { min: number; max: number; step: number };

type DomainOption = { value: string; label: string };

type Props = {
  label: string;
  scale: Scale;
  from: number | undefined;
  to: number | undefined;
  domains?: readonly DomainOption[];
  domain?: string;
  boundsFor?: (domain: string) => { from?: number; to?: number };
  onApply: (bounds: { from: number; to: number | undefined; domain?: string }) => void;
  onCancel: () => void;
  onBack: () => void;
};

function clamp(raw: string, scale: Scale, fallback: number): number {
  const value = Number(raw);
  if (raw.trim() === '' || Number.isNaN(value)) return fallback;
  return Math.max(scale.min, Math.min(scale.max, value));
}

function round(value: number): number {
  return Number(value.toFixed(2));
}

function snap(value: number, scale: Scale): number {
  const steps = Math.round((value - scale.min) / scale.step);
  return round(scale.min + steps * scale.step);
}

export function RangeValuePicker({
  label,
  scale,
  from,
  to,
  domains,
  domain,
  boundsFor,
  onApply,
  onCancel,
  onBack,
}: Props) {
  const [lower, setLower] = useState(String(from ?? scale.min));
  const [upper, setUpper] = useState(String(to ?? scale.max));
  const [chosenDomain, setChosenDomain] = useState(domain ?? domains?.[0]?.value);
  const [lowerDirty, setLowerDirty] = useState(false);
  const [upperDirty, setUpperDirty] = useState(false);

  const switchDomain = (next: string) => {
    setChosenDomain(next);
    if (boundsFor == null) return;
    const saved = boundsFor(next);
    if (!lowerDirty) setLower(String(saved.from ?? scale.min));
    if (!upperDirty) setUpper(String(saved.to ?? scale.max));
  };

  const apply = () => {
    const a = snap(clamp(lower, scale, scale.min), scale);
    const b = snap(clamp(upper, scale, scale.max), scale);
    const [low, high] = a > b ? [b, a] : [a, b];
    onApply({
      from: low,
      to: high >= scale.max ? undefined : high,
      ...(chosenDomain ? { domain: chosenDomain } : {}),
    });
  };

  const field = (
    name: 'From' | 'To',
    value: string,
    setValue: (next: string) => void,
    markDirty: () => void,
  ) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
      <Box
        component="label"
        htmlFor={`range-${name}`}
        sx={{ fontFamily: ROSTER.font, fontSize: 12, fontWeight: 700, color: ROSTER.muted }}
      >
        {name}
      </Box>
      <Box
        component="input"
        id={`range-${name}`}
        type="number"
        min={scale.min}
        max={scale.max}
        step={scale.step}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          markDirty();
          setValue(e.target.value);
        }}
        sx={{
          width: '100%',
          p: '6px 8px',
          border: `1px solid ${ROSTER.hairline}`,
          borderRadius: '6px',
          fontFamily: ROSTER.font,
          fontSize: 14,
          color: ROSTER.ink,
          '&:focus-visible': { outline: `2px solid ${ROSTER.ink}`, outlineOffset: '1px' },
        }}
      />
    </Box>
  );

  return (
    <Box data-testid="range-picker">
      <Box
        component="button"
        type="button"
        data-testid="range-picker-back"
        aria-label={`Back to filter list from ${label}`}
        onClick={onBack}
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          p: '11px 14px',
          border: 0,
          borderBottom: `1px solid ${ROSTER.hairline}`,
          background: 'transparent',
          fontFamily: ROSTER.font,
          fontSize: 13,
          fontWeight: 700,
          color: (theme) => theme.palette.primary.main,
          cursor: 'pointer',
        }}
      >
        {`‹ ${label}`}
      </Box>
      <Box sx={{ p: '14px' }} role="group" aria-label={`${label} range`}>
        {domains != null && (
          <Box sx={{ display: 'flex', gap: '6px', mb: '12px' }}>
            {domains.map((option) => {
              const active = option.value === chosenDomain;
              return (
                <Box
                  key={option.value}
                  component="button"
                  type="button"
                  data-testid="domain-choice"
                  aria-pressed={active}
                  onClick={() => switchDomain(option.value)}
                  sx={{
                    flex: 1,
                    p: '7px 0',
                    borderRadius: '7px',
                    border: (theme) =>
                      `1px solid ${active ? theme.palette.primary.main : ROSTER.hairline}`,
                    bgcolor: (theme) =>
                      active ? alpha(theme.palette.primary.main, 0.08) : '#FFFFFF',
                    fontFamily: ROSTER.font,
                    fontSize: 13,
                    fontWeight: 700,
                    color: (theme) => (active ? theme.palette.primary.main : ROSTER.muted),
                    cursor: 'pointer',
                  }}
                >
                  {option.label}
                </Box>
              );
            })}
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
          {field('From', lower, setLower, () => setLowerDirty(true))}
          <Box
            sx={{
              fontFamily: ROSTER.font,
              fontSize: 12,
              color: ROSTER.muted,
              pb: '8px',
            }}
          >
            to
          </Box>
          {field('To', upper, setUpper, () => setUpperDirty(true))}
        </Box>
        <Box
          sx={{ mt: '8px', fontFamily: ROSTER.font, fontSize: 12, color: ROSTER.muted }}
        >
          {`Scale ${scale.min}–${scale.max} · steps of ${scale.step}`}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', mt: '12px' }}>
          <Box
            component="button"
            type="button"
            data-testid="range-picker-cancel"
            onClick={onCancel}
            sx={{
              p: '7px 13px',
              border: 0,
              background: 'transparent',
              borderRadius: '6px',
              fontFamily: ROSTER.font,
              fontSize: 12,
              fontWeight: 700,
              color: ROSTER.muted,
              cursor: 'pointer',
            }}
          >
            Cancel
          </Box>
          <Box
            component="button"
            type="button"
            data-testid="range-picker-apply"
            onClick={apply}
            sx={{
              p: '7px 13px',
              border: 0,
              borderRadius: '6px',
              fontFamily: ROSTER.font,
              fontSize: 12,
              fontWeight: 700,
              color: '#FFFFFF',
              bgcolor: (theme) => theme.palette.primary.main,
              cursor: 'pointer',
            }}
          >
            Apply
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
